import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Search, Loader2, ExternalLink, Sparkles } from 'lucide-react'
import { useWireAuth } from '../auth/useWireAuth'

// ── Types ────────────────────────────────────────────────────────────────────

type SourceDocument = {
  id: string
  title: string
}

type SearchResult = {
  question: string
  answer: string
  thinking: string
  sources: SourceDocument[]
}

type ConversationEntry =
  | { type: 'question'; text: string }
  | { type: 'result'; result: SearchResult }

// ── Helpers ──────────────────────────────────────────────────────────────────

const CAFE_API_URL =
  (import.meta.env.VITE_CAFE_API_URL as string | undefined) ??
  'https://cafe-prod.whiteboardfederalrewired.com'

function parseSourceDocuments(raw: unknown[]): SourceDocument[] {
  return raw.map((d: unknown) => {
    // LangChain serialized format: { kwargs: { metadata: { id, title } } }
    const meta = (d as { kwargs?: { metadata?: { id?: unknown; title?: unknown } } })
      ?.kwargs?.metadata
    return {
      id: String(meta?.id ?? ''),
      title: String(meta?.title ?? 'Unknown document'),
    }
  })
}

async function queryAi(
  prompt: string,
  userId: string,
  cid: string,
): Promise<{ answer: string; thinking: string; sources: SourceDocument[] }> {
  const res = await fetch(CAFE_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, action: 'prompt', user: userId, cid }),
  })
  if (!res.ok) throw new Error(`Request failed: ${res.status}`)
  const data = await res.json()
  const sources = parseSourceDocuments(
    Array.isArray(data.source_documents) ? data.source_documents : [],
  )
  const raw = String(data.answer ?? '')
  const thinkingMatch = raw.match(/<thinking>([\s\S]*?)<\/thinking>/i)
  const thinking = thinkingMatch ? thinkingMatch[1].trim() : ''
  return {
    answer: raw
      .replace(/<thinking>[\s\S]*?<\/thinking>/gi, '')
      .replace(/<\/?response>/gi, '')
      .trim(),
    thinking,
    sources,
  }
}

// ── Typewriter hook ─────────────────────────────────────────────────────────

function useTypewriter(text: string, speed = 4) {
  const [displayed, setDisplayed] = useState('')
  useEffect(() => {
    setDisplayed('')
    if (!text) return
    let i = 0
    const timer = setInterval(() => {
      i++
      setDisplayed(text.slice(0, i))
      if (i >= text.length) clearInterval(timer)
    }, speed)
    return () => clearInterval(timer)
  }, [text, speed])
  return displayed
}

// ── Sub-components ───────────────────────────────────────────────────────────

function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end pt-6">
      <div className="max-w-[72%] rounded-2xl rounded-tr-sm bg-[var(--color-violet-4)] border border-[var(--color-violet-6)] px-4 py-2.5 font-sans text-sm leading-relaxed text-[var(--color-violet-11)]">
        {text}
      </div>
    </div>
  )
}

function ThinkingBlock({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false)
  if (!text) return null
  const firstLine = text.split('\n').find((l) => l.trim()) ?? text
  return (
    <div className="rounded border border-line bg-fill px-4 py-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
        aria-expanded={expanded}
      >
        <span className="font-mono text-[9px] font-bold uppercase tracking-widest text-fg-secondary/50">
          Reasoning
        </span>
        <span className="font-mono text-[9px] text-fg-secondary/40">{expanded ? '▲ collapse' : '▼ expand'}</span>
      </button>
      <p className="mt-2 font-mono text-[11px] leading-relaxed text-fg-secondary/60 whitespace-pre-wrap">
        {expanded ? text : <span className="line-clamp-1">{firstLine}</span>}
      </p>
    </div>
  )
}

function SourceCard({ source }: { source: SourceDocument }) {
  const href = source.id ? `https://en.wikipedia.org/?curid=${source.id}` : undefined
  const Tag = href ? 'a' : 'div'
  return (
    <Tag
      {...(href ? { href, target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="flex items-start gap-3 rounded border border-line bg-surface px-4 py-3 transition-colors hover:border-line-strong hover:bg-fill"
    >
      <ExternalLink size={13} className="mt-0.5 shrink-0 text-fg-secondary" aria-hidden="true" />
      <div className="min-w-0">
        <p className="truncate text-[12px] font-semibold text-fg">{source.title}</p>
        <p className="mt-0.5 font-mono text-[10px] text-fg-secondary">DOC {source.id}</p>
      </div>
    </Tag>
  )
}

function ResultBlock({ result }: { result: SearchResult }) {
  const displayed = useTypewriter(result.answer)
  const isDone = displayed.length >= result.answer.length

  return (
    <div className="mt-5 space-y-5">
      {/* Reasoning */}
      {result.thinking && <ThinkingBlock text={result.thinking} />}

      {/* Sources first */}
      {result.sources.length > 0 && (
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
            Sources
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {result.sources.map((s) => (
              <SourceCard key={s.id} source={s} />
            ))}
          </div>
        </div>
      )}

      {/* Answer */}
      <div>
        <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
          Analysis
        </p>
        <p className="font-serif text-base leading-relaxed text-fg whitespace-pre-wrap">
          {displayed}
          {!isDone && (
            <span className="ml-px inline-block h-[1em] w-[2px] translate-y-[1px] animate-pulse bg-fg" aria-hidden="true" />
          )}
        </p>
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function AiSearchPage() {
  const { user } = useWireAuth()
  const userId = user?.sub ?? user?.username ?? 'anon'
  const cidRef = useRef(crypto.randomUUID())

  const [query, setQuery] = useState('')
  const [entries, setEntries] = useState<ConversationEntry[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasStarted, setHasStarted] = useState(false)
  const resultsEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const pageRef = useRef<HTMLDivElement>(null)
  const [pageTop, setPageTop] = useState(0)

  useLayoutEffect(() => {
    const measure = () => {
      if (pageRef.current) {
        setPageTop(pageRef.current.getBoundingClientRect().top)
      }
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  const isEmpty = query.trim().length === 0

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const prompt = query.trim()
    if (!prompt || isLoading) return

    if (!hasStarted) setHasStarted(true)

    setIsLoading(true)
    setError(null)
    setQuery('')
    if (textareaRef.current) textareaRef.current.style.height = 'auto'

    // Show the user's bubble immediately
    setEntries((prev) => [...prev, { type: 'question', text: prompt }])

    try {
      const { answer, thinking, sources } = await queryAi(prompt, userId, cidRef.current)
      setEntries((prev) => [...prev, { type: 'result', result: { question: prompt, answer, thinking, sources } }])
      setTimeout(() => {
        resultsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }, 50)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      ref={pageRef}
      className="flex flex-col overflow-hidden"
      style={{ height: `calc(100dvh - ${pageTop}px)` }}
    >

      {/* ── Main area: hero OR results ── */}
      <div className="relative flex-1 overflow-hidden">

        {/* Hero — centered, fades out on first submit */}
        <div
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-start gap-6 px-6 pt-[20vh] transition-opacity duration-500"
          style={{ opacity: hasStarted ? 0 : 1, pointerEvents: hasStarted ? 'none' : 'auto' }}
          aria-hidden={hasStarted}
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-full border border-line bg-surface shadow-sm">
            <Sparkles size={26} className="text-fg-secondary" aria-hidden="true" />
          </div>
          <div className="text-center">
            <h1 className="font-serif text-4xl font-bold tracking-tight text-fg">HotWIRe AI</h1>
            <p className="mt-2 text-sm text-fg-secondary">
              Ask a question and get a synthesized answer drawn from the intelligence corpus.
            </p>
          </div>
          {/* Hero input */}
          <form
            onSubmit={handleSubmit}
            className="flex w-full max-w-xl items-end gap-3"
          >
            <textarea
              ref={textareaRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                e.target.style.height = 'auto'
                e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  if (!isEmpty) handleSubmit(e as unknown as FormEvent)
                }
              }}
              placeholder="Ask a question…"
              rows={1}
              disabled={isLoading}
              className="flex-1 resize-none overflow-hidden rounded-lg border border-line bg-fill px-4 py-2.5 text-sm text-fg placeholder:text-fg-secondary outline-none focus:border-accent-primary-line focus:ring-1 focus:ring-accent-primary-line disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={isEmpty || isLoading}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-solid text-white transition-opacity hover:bg-solid-hover disabled:opacity-40"
              aria-label="Submit question"
            >
              {isLoading
                ? <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                : <Search size={16} aria-hidden="true" />
              }
            </button>
          </form>
        </div>

        {/* Results — scrollable, fades in on first submit */}
        <div
          className="absolute inset-0 overflow-y-auto transition-opacity duration-300"
          style={{ opacity: hasStarted ? 1 : 0, pointerEvents: hasStarted ? 'auto' : 'none' }}
        >
          <div className="mx-auto max-w-4xl px-6 pb-8 pt-8">
            {entries.map((entry, i) =>
              entry.type === 'question'
                ? <UserBubble key={i} text={entry.text} />
                : <ResultBlock key={i} result={entry.result} />
            )}

            {isLoading && (
              <div className="flex items-center gap-3 py-8 text-sm text-fg-secondary">
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                Analyzing…
              </div>
            )}

            {error && (
              <div className="mt-6 rounded border border-error-line bg-error-fill px-4 py-3 text-sm text-error-fg">
                {error}
              </div>
            )}

            <div ref={resultsEndRef} />
          </div>
        </div>
      </div>

      {/* ── Bottom input bar — fades in after first submit ── */}
      <div
        className="shrink-0 border-t border-line bg-canvas/95 backdrop-blur-sm transition-opacity duration-500"
        style={{ opacity: hasStarted ? 1 : 0, pointerEvents: hasStarted ? 'auto' : 'none' }}
      >
        <form
          onSubmit={handleSubmit}
          className="mx-auto flex max-w-2xl items-end gap-3 px-6 py-4"
        >
          <textarea
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              e.target.style.height = 'auto'
              e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (!isEmpty) handleSubmit(e as unknown as FormEvent)
              }
            }}
            placeholder="Ask a follow-up…"
            rows={1}
            disabled={isLoading}
            className="flex-1 resize-none overflow-hidden rounded-lg border border-line bg-fill px-4 py-2.5 text-sm text-fg placeholder:text-fg-secondary outline-none focus:border-accent-primary-line focus:ring-1 focus:ring-accent-primary-line disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={isEmpty || isLoading}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-solid text-white transition-opacity hover:bg-solid-hover disabled:opacity-40"
            aria-label="Submit question"
          >
            {isLoading
              ? <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              : <Search size={16} aria-hidden="true" />
            }
          </button>
        </form>
      </div>
    </div>
  )
}
